import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import AdPlanForm from "./AdPlanForm";
import useAdPlanState from "./useAdPlanState";
import { useCreateAdPlan } from "./useCreateAdPlan";
import { useUpdateAdPlan } from "./useUpdateAdPlan";
import { Modal } from "../../basic/Modal";
import { useAppSelector } from "../../../../redux/hooks";

const AdPlanChild = (props) => {
  const { t } = useTranslation(["advertising", "common"]);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const user = useAppSelector((state) => state.user);
  const { adPlan, errors, isValid, onChange, brandOptions, savedVideos, storeOptions, pageOptions } = useAdPlanState(
    { ...props.initialChild, canHaveChildren: false, hasParent: true },
    marketPlace,
    props.categoryId,
    null,
    props.roas,
    user.organization_type,
  );
  return (
    <Modal
      isOpen={props.show}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          props.onCancel();
        }
      }}
      title={t(($) => $.advertising["Create a New Media Plan"])}
    >
      {props.loading ?
        t(($) => $.common["Loading..."])
      : <AdPlanForm
          onChange={onChange}
          state={adPlan}
          errors={errors}
          isValid={isValid}
          categories={props.categories}
          dashboard={false}
          user={user}
          marketPlace={marketPlace}
          channel={localStorage.channel}
          savedVideos={savedVideos}
          brandOptions={brandOptions}
          storeOptions={storeOptions}
          pageOptions={pageOptions}
          isChild
          parentDailyBudget={props.parentDailyBudget}
        />
      }
    </Modal>
  );
};

const AdPlanPanel = (props) => {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const user = useAppSelector((state) => state.user);
  const {
    adPlan,
    errors,
    isValid,
    onChange,
    clear,
    brandOptions,
    savedVideos,
    storeOptions,
    pageOptions,
    loading,
    agreeToArchive,
    setAgreeToArchive,
    apiStatus,
  } = useAdPlanState(
    {},
    marketPlace,
    props.categoryId,
    props.adId,
    props.roas,
    user.organization_type,
    props.isAdArchived,
  );
  const createAdPlan = useCreateAdPlan();
  const updateAdPlan = useUpdateAdPlan({ adId: props.adId });

  const [currentChild, setCurrentChild] = useState(null);

  const formattedCategories = useMemo(
    () =>
      props.categories?.map((cat) => ({
        value: cat.id ?? cat.value,
        label: cat.leaf_category_name ?? cat.label,
      })) ?? [],
    [props.categories],
  );

  const handleCreate = async (adPlan, notify = true) => {
    try {
      await createAdPlan.mutateAsync({ adPlan, adId: props.adId, notify });
      return true;
    } catch {
      return undefined;
    }
  };

  const handleUpdate = (adPlan) => {
    updateAdPlan.mutate({ adPlan });
  };

  useEffect(() => {
    clear();

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [props.categoryId]);

  return (
    <>
      {}
      {!!currentChild && (
        <AdPlanChild
          {...props}
          categories={formattedCategories}
          show={!!currentChild}
          onCancel={() => {
            setCurrentChild(null);
          }}
          initialChild={currentChild.child || { categoryId: props.categoryId }}
          parentDailyBudget={adPlan.dailyBudget}
        />
      )}
      <AdPlanForm
        loading={loading}
        adId={props.adId}
        onChange={onChange}
        state={adPlan}
        errors={errors}
        isValid={isValid}
        onShowChild={(i, child) => {
          setCurrentChild({ i, child });
        }}
        categories={formattedCategories}
        savedVideos={savedVideos}
        brandOptions={brandOptions}
        storeOptions={storeOptions}
        pageOptions={pageOptions}
        user={user}
        marketPlace={marketPlace}
        channel={localStorage.channel}
        isChild={adPlan.parent}
        agreeToArchive={agreeToArchive}
        setAgreeToArchive={setAgreeToArchive}
        apiStatus={apiStatus}
        onUpdate={handleUpdate}
        onCreate={handleCreate}
        isProcessing={createAdPlan.isPending || updateAdPlan.isPending}
        isAdArchived={props.isAdArchived}
      />
    </>
  );
};

export default AdPlanPanel;
